/**
 * Copyright 2023-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { FormProvider } from "react-hook-form";
import { Else, If, Then } from "react-if";
import { Label, Text, theme } from "@nordcloud/gnui";
import { ExecutionPolicy } from "~/generated/graphql";
import { getFirstItem } from "~/tools";
import { SystemActionNames } from "~/views/plans/PlanCreate/components/PlanCreateWizard/constants";
import { usePlanWizard } from "~/views/plans/PlanCreate/components/PlanCreateWizard/PlanProvider";
import { useFormDefaultValues } from "~/views/plans/PlanCreate/components/PlanCreateWizard/PlanSettingsForms/components/ActionSettingsForm/defaultFormValues";
import { useStepState } from "~/views/plans/PlanCreate/components/PlanCreateWizard/PlanSettingsForms/StepProvider";
import { usePlanSettingsFieldHandlers } from "./hooks/usePlanSettingsFieldHandlers";
import { executionPolicy, resourceOperation } from "./options";
import { PlanSettingsSections } from "./PlanSettingsSections";
import { WindowDuration } from "./WindowDuration";

type Props = {
  nextStep?: () => void;
  setTab: (key: number) => void;
};

export function ActionSettingsForm({ nextStep, setTab }: Props) {
  const { stepState, updateStepState } = useStepState();
  const { setPlanData } = usePlanWizard();

  const executionItem = getFirstItem(stepState.entities ?? []);
  const listId = executionItem.listId;
  const isFirstEntity = listId === stepState.selectedEntity?.listId;
  const isBatchAction = !!stepState.selectedEntity?.batchId;

  const formMethods = useFormDefaultValues(stepState);

  const { control, handleSubmit, setValue } = formMethods;
  const { handleChange, handleExecutionPolicyChange } =
    usePlanSettingsFieldHandlers({
      stepState,
      updateStepState,
      setPlanData,
      setValue,
    });

  const submit = () => {
    if (stepState.selectedEntity?.notificationGroups) {
      setTab(2);
    } else {
      nextStep?.();
    }
  };

  const finalExecutionPolicy = executionPolicy.map((policy) => ({
    ...policy,
    disabled:
      (isBatchAction && policy.value !== ExecutionPolicy.Anyway) ||
      (!isBatchAction &&
        isFirstEntity &&
        policy.value === ExecutionPolicy.SuccessOrApproval),
  }));

  const modifiedResourceOperation = resourceOperation.map((operation) => ({
    ...operation,
    disabled: isBatchAction && operation.value,
  }));

  return (
    <FormProvider {...formMethods}>
      <form
        id="planSettingsForm"
        onSubmit={handleSubmit(submit)}
        css={{ minHeight: "13rem" }}
      >
        <If
          condition={
            stepState.selectedEntity?.actionName === SystemActionNames.DELAY
          }
        >
          <Then>
            <Label css={{ marginBottom: "0" }} htmlFor="delay" name="Delay" />
            <Text mb={theme.spacing.spacing02} size="sm">
              Next action will start after this time has passed.
            </Text>
            <WindowDuration control={control} handleChange={handleChange} />
          </Then>
          <Else>
            <PlanSettingsSections
              control={control}
              executionPolicies={finalExecutionPolicy}
              handleChange={handleChange}
              handleExecutionPolicyChange={handleExecutionPolicyChange}
              helperTextColor={theme.color.text.text02}
              resourceOperations={modifiedResourceOperation}
              showWindowSettings={!isBatchAction}
              windowDurationLabel="Action Window Duration"
            />
          </Else>
        </If>
      </form>
    </FormProvider>
  );
}
